/**
 * 첨부파일 서비스
 * 책에 연결된 첨부파일(PDF 등) 목록 조회 및 다운로드 기능 제공
 */

import apiClient from '../../core/http/axios';

// 파일 정보 타입
interface FileInfo {
  file_uuid: string;
  filename: string;
  content_type: string;
  size: number;
}

// API 첨부파일 항목 타입
interface AttachmentItem {
  attachment_id: number;
  purpose: string;
  visibility: string;
  created_at: string;
  file: FileInfo;
  professor_name: string;
}

// 외부 노출용 첨부파일 타입 (간소화)
export interface Attachment {
  attachment_id: number;
  file_uuid: string;
  file_name: string;
  file_size: number;
  file_type: string;
  created_at: string;
  purpose: string;
  professor_name: string;
}

// API 응답 타입
interface AttachmentListResponse {
  success: boolean;
  error: string | null;
  data: {
    total: number;
    limit: number;
    offset: number;
    items: AttachmentItem[];
  };
  code: string | null;
}

/**
 * 첨부파일 서비스
 */
export const attachmentService = {
  /**
   * 책의 첨부파일 목록 조회
   * @param bookUid 책 UUID
   * @param limit 조회 개수 (기본값: 50)
   * @param offset 시작 위치 (기본값: 0)
   */
  getAttachments: async (
    bookUid: string, 
    limit: number = 50, 
    offset: number = 0
  ): Promise<Attachment[]> => {
    try {
      // console.log('[Attachment] API 호출:', `/viewer/books/${bookUid}/attachments`);
      const response = await apiClient.get<AttachmentListResponse>(
        `/viewer/books/${bookUid}/attachments`,
        {
          params: {
            include_deleted: false,
            limit,
            offset,
          },
        }
      );
      
      // console.log('[Attachment] API 응답:', response.data);
      
      if (!response.data.success) {
        // console.error('[Attachment] 목록 조회 실패:', response.data.error);
        return [];
      }
      
      // API 응답을 간소화된 형태로 변환
      const items = response.data.data?.items || [];
      // console.log('[Attachment] items 개수:', items.length);
      
      return items.map((item): Attachment => ({
        attachment_id: item.attachment_id,
        file_uuid: item.file.file_uuid,
        file_name: item.file.filename,
        file_size: item.file.size,
        file_type: item.file.content_type,
        created_at: item.created_at,
        purpose: item.purpose,
        professor_name: item.professor_name || '',
      }));
    } catch (error) {
      // console.error('[Attachment] 목록 조회 오류:', error);
      return [];
    }
  },

  /**
   * 첨부파일 다운로드/뷰어 API 경로 반환
   * @param fileUuid 파일 UUID
   */
  getFileUrl: (fileUuid: string): string => {
    // API 경로: /viewer/files/{file_uuid}/download (apiClient가 baseURL 처리)
    return `/viewer/files/${fileUuid}/download`;
  },

  /**
   * 첨부파일 전체 URL 반환 (새 창 열기용)
   * @param fileUuid 파일 UUID
   */
  getFullFileUrl: (fileUuid: string): string => {
    const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://bookapi.hakjisa.kr').replace(/\/$/, '');
    return `${baseUrl}/api/viewer/files/${fileUuid}/download`;
  },

  /**
   * 첨부파일 다운로드 (직접 다운로드)
   * @param fileUuid 파일 UUID
   * @param fileName 저장할 파일명
   */
  downloadFile: async (fileUuid: string, fileName: string): Promise<void> => {
    try {
      const downloadUrl = attachmentService.getFullFileUrl(fileUuid);
      // console.log('[Attachment] 다운로드 URL:', downloadUrl, 'fileName:', fileName);

      // 새 창에서 다운로드
      window.open(downloadUrl, '_blank');
    } catch (error) {
      // console.error('[Attachment] 다운로드 실패:', error);
      throw error;
    }
  },

  /**
   * 파일 크기 포맷팅
   * @param bytes 바이트 수
   */
  formatFileSize: (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },
};

export default attachmentService;
